/**
 * ErrorBoundary — last line of defence against a render crash.
 *
 * React unmounts the whole tree when a render throws, leaving a blank page. This
 * boundary catches that and shows a recoverable message instead, so one bad
 * value can't take the entire app down. Wrap it around screen content (inside
 * the chrome) so the header/footer survive.
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console for diagnostics (and any error-tracking wired later).
    console.error("Unhandled render error:", error, info.componentStack);
  }

  private handleReload = () => {
    this.setState({ hasError: false });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-error" role="alert">
          <h1 className="app-error__title">Qualcosa è andato storto</h1>
          <p className="app-error__body">
            Si è verificato un errore imprevisto nel caricamento della pagina.
          </p>
          <button type="button" className="overview__retry" onClick={this.handleReload}>
            Ricarica
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
